/**
 * Pawn Component Factory
 *
 * Creates color-specific pawn components using a factory pattern to eliminate code duplication.
 * All 4 color variants (Red, Blue, Yellow, Green) are generated from a single implementation.
 */

import React from 'react';
import type { PawnProps } from '../../constants/types';
import PawnComponent from './PawnComponent';
import { PAWN_COLOR_SCHEMES, type PawnColorScheme } from './constants/pawnColorSchemes';

type PawnColor = 'red' | 'blue' | 'yellow' | 'green';

/**
 * Factory function to create a memoized color-specific pawn component
 */
function createColoredPawn(color: PawnColor) {
  const colorScheme: PawnColorScheme = PAWN_COLOR_SCHEMES[color];
  const displayName = `${color.charAt(0).toUpperCase() + color.slice(1)}Pawn`;

  const ColoredPawn = React.memo(function ColoredPawn(props: PawnProps) {
    return <PawnComponent {...props} colorScheme={colorScheme} />;
  });

  ColoredPawn.displayName = displayName;
  return ColoredPawn;
}

// Generate all 4 color variants
export const RedPawn = createColoredPawn('red');
export const BluePawn = createColoredPawn('blue');
export const YellowPawn = createColoredPawn('yellow');
export const GreenPawn = createColoredPawn('green');
